import { createFileRoute } from "@tanstack/react-router";
import { LocalServicePage } from "@/components/LocalServicePage";
import __apexTheater from "@/assets/Apex_Theater.webp.asset.json";
const hero = __apexTheater.url;
import apexTheaterRichImg from "@/assets/Apex_Theater.webp.asset.json";
import { localBusinessSchema, NEIGHBORHOOD_GEO } from "@/lib/localBusinessSchema";

const NEIGHBORHOOD = "Castle Rock";
const SLUG = "castle-rock";
const ZIP = "80108";
const URL = "https://apexav.com/castle-rock/home-theater-installation";

const faqs = [
  {
    q: "How much does a Castle Rock home theater installation cost?",
    a: "Castle Rock projects typically start around $30,000 for a finished basement media room with 4K laser projection, 7.2.4 Dolby Atmos surround, motorized screen, and acoustic treatment. The Castle Pines Village and Keene Ranch estate builds we do most often run $75,000–$250,000+ with full immersive engineering, tiered seating, and integrated lighting and shading. Every project is fixed-bid before any work starts.",
  },
  {
    q: "Can you build a theater in a Castle Rock new-construction basement?",
    a: "Yes — and pre-wiring during framing saves thousands. Most new builds in The Meadows, Cobblestone Ranch, and Crystal Valley have 9-foot basement ceilings that support a full 9.4.4 Atmos system with in-ceiling height channels. We coordinate directly with your builder on conduit, structured wiring, and HVAC routing before drywall closes up the walls.",
  },
  {
    q: "Do you handle Castle Pines Village estates?",
    a: "Frequently. The gated estates in Castle Pines Village have the ceiling height, square footage, and budget for true reference theaters with full acoustic engineering — decoupled walls, double drywall, isolated HVAC ducting, and acoustically transparent fabric walls. We work with your architect and interior designer so the theater integrates invisibly with the rest of the home.",
  },
  {
    q: "Will my new theater work with my existing smart home system?",
    a: "Yes. We add the new theater as another zone on your existing platform — same remote, same app, same scenes. Lights, shades, climate, security, and AV all respond to one button. If you don't have a control system yet, we'll spec the right platform as part of the same project.",
  },
  {
    q: "How long does a Castle Rock theater install take?",
    a: "Plan on 8–14 weeks for a dedicated room: 2–3 weeks of design and engineering, 4–8 weeks for construction, and 2–3 weeks for AV install, calibration, and programming. Media-room installs without construction typically wrap in 3–5 weeks. New-construction pre-wires happen in parallel with your builder's schedule.",
  },
];

const testimonials = [
  { quote: "Apex pre-wired our new build in The Meadows during framing and finished the theater after move-in. Cleanest install we've ever seen and the picture is unreal.", name: "Jason M.", location: "The Meadows, Castle Rock" },
  { quote: "Our Castle Pines Village basement is now a true reference theater — 9.4.6 Atmos, 4K laser, tiered seating. The acoustic treatment completely disappears behind the fabric walls.", name: "Rebecca H.", location: "Castle Pines Village" },
  { quote: "Crystal Valley basement media room turned out perfect. One button drops the screen, dims the lights, and starts the movie.", name: "Tom S.", location: "Crystal Valley" },
];

export const Route = createFileRoute("/castle-rock/home-theater-installation")({
  head: () => ({
    meta: [
      { title: "Home Theater Installation Castle Rock | Apex AV Denver" },
      { name: "description", content: "Custom home theater design & installation in Castle Rock and The Meadows. Dolby Atmos theaters from $30K. Free in-home consultation." },
      { name: "keywords", content: "home theater installation Castle Rock, custom home theater Castle Rock, Castle Pines Village home theater, The Meadows home theater installer, Dolby Atmos Castle Rock, basement theater Castle Rock, media room installation Castle Rock, 4K projector installation Castle Rock" },
      { property: "og:title", content: "Home Theater Installation Castle Rock — Apex AV" },
      { property: "og:description", content: "Reference-grade home theater design & installation in Castle Rock & Castle Pines." },
      { property: "og:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
      { property: "og:image:alt", content: "Apex AV custom home theater installation in Denver, Colorado" },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
      { name: "twitter:title", content: "Home Theater Installation Castle Rock — Apex AV" },
      { name: "twitter:description", content: "Reference-grade home theater design & installation in Castle Rock & Castle Pines." },
    ],
    links: [{ rel: "canonical", href: URL }, { rel: "preload", as: "image", href: hero, fetchPriority: "high" } as never],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              name: `Home Theater Installation in ${NEIGHBORHOOD}`,
              description: `Custom home theater design and installation in ${NEIGHBORHOOD}, CO.`,
              url: URL,
              provider: { "@id": "https://apexav.com/#organization" },
              areaServed: { "@type": "Place", name: `${NEIGHBORHOOD}, CO ${ZIP}` },
              serviceType: "Custom Home Theater Installation",
              offers: { "@type": "Offer", priceCurrency: "USD", price: "30000" },
            },
            localBusinessSchema({
              neighborhood: NEIGHBORHOOD,
              url: URL,
              image: `https://apexav.com${apexTheaterRichImg.url}`,
              zip: ZIP,
              latitude: NEIGHBORHOOD_GEO[SLUG].latitude,
              longitude: NEIGHBORHOOD_GEO[SLUG].longitude,
              priceFloor: 30000,
              serviceName: "Home Theater Installation",
            }),
            {
              "@type": "FAQPage",
              mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }),
      },
    ],
  }),
  component: () => (
    <LocalServicePage
      neighborhood={NEIGHBORHOOD}
      neighborhoodSlug={SLUG}
      zip={ZIP}
      service="home theater installation"
      serviceKey="home-theater"
      parentServiceLabel="home theater"
      parentServiceTo="/services/home-theater"
      h1="Home Theater Installation in Castle Rock, CO"
      intro="Reference-grade dedicated theaters and finished basement media rooms designed for Castle Rock estates and new builds — from Castle Pines Village and Keene Ranch to The Meadows, Cobblestone Ranch, and Crystal Valley. Dolby Atmos surround, 4K and 8K laser projection, motorized screens, and acoustic treatment that disappears behind the architecture."
      hero={hero}
      bullets={[
        "New-construction pre-wire coordination with your builder",
        "9.4.6 Dolby Atmos design & calibration",
        "4K and 8K laser projection (JVC, Sony, Barco)",
        "Hidden in-wall speakers and equipment racks",
        "Integrated lighting and automation scenes",
        "Free in-home design consultation",
      ]}
      microNeighborhoods={["Castle Pines Village", "The Meadows", "Cobblestone Ranch", "Crystal Valley", "Keene Ranch", "Sapphire Pointe"]}
      testimonials={testimonials}
      faqs={faqs}
      bodyParagraphs={[
        {
          heading: "Built for Castle Rock new builds and estates",
          body: (
            <>
              <p>
                Castle Rock spans everything from production builds in The Meadows and Cobblestone Ranch with 9-foot basement ceilings, to gated Castle Pines Village estates with vaulted lower levels, to custom Keene Ranch builds on multi-acre lots. A theater design that works in one will fail in another — which is why we engineer every Castle Rock install around the specific structure, footprint, and finish palette.
              </p>
              <p>
                Our typical Castle Rock project starts with a free in-home walkthrough where we measure the room, listen to how you actually watch, and review your existing or planned control system. From there we deliver a fixed-scope design — drawings, equipment list, finish schedule, and price — before any construction begins.
              </p>
            </>
          ),
        },
        {
          heading: "What a Castle Rock theater install includes",
          body: (
            <>
              <p>
                A reference-grade Castle Rock theater is more than a projector and speakers. We design and install the full system: object-based <strong>Dolby Atmos surround</strong> (typically 7.2.4 or 9.4.6), <strong>4K or 8K laser projection</strong> from JVC, Sony, or Barco, motorized screens from Stewart or Screen Innovations, acoustic treatment hidden behind acoustically transparent fabric walls, tiered theater seating with bass shakers, fiber-optic starfield ceilings, and one-button scenes that dim the lights, drop the shades, and fire the projector.
              </p>
              <p>
                For Castle Pines Village and Keene Ranch estates we frequently spec full <strong>acoustic engineering</strong> — decoupled walls, RSIC clips, double drywall, isolated HVAC ducting — so the theater performs at reference SPL without bleeding sound to the rest of the house. For new builds in The Meadows and Crystal Valley, pre-wiring during framing is the single biggest cost-saver and lets us run conduit, fiber, and structured cabling before drywall closes up the walls.
              </p>
            </>
          ),
        },
        {
          heading: "Integrated with the rest of your Castle Rock smart home",
          body: (
            <>
              <p>
                Most Castle Rock homes we touch already have or are planning a smart home system. The theater becomes another zone on the same platform — same remote, same app, same scenes. If you don't have a control system yet, we'll spec the right one as part of the same project — see our <a href="/castle-rock/smart-home-installation" className="text-brand-text underline underline-offset-2">Castle Rock smart home installation</a> page for the full scope.
              </p>
            </>
          ),
        },
      ]}
    />
  ),
});
